import { t } from "../i18n/index.js";
import { layoutTouch, readSafeInsets, touchPrefs } from "../input/touchLayout.js";
import "./touchCoach.css";

/**
 * Aprender el mando tactil sin leer un manual.
 *
 * Un pulgar fantasma enseña cada gesto EN SU SITIO y en el momento en que
 * sirve: el joystick al empezar, y el golpeo la primera vez que el balon es
 * tuyo (el boton, y que tocar el campo lo manda ahi). Cada pista se va en
 * cuanto haces el gesto, y no vuelve: lo aprendido se guarda en este
 * navegador. Nunca atrapa un toque (`pointer-events: none`): enseña encima
 * del partido, no lo para.
 */
export type CoachStep = "move" | "strike";

const DONE_KEY = "fynta.touchCoach";
/** Si no lo haces, la pista se aparta sola: enseñar no puede ser tapar. */
const HINT_MS = 9000;

function loadDone(): Set<CoachStep> {
  try {
    const raw = JSON.parse(localStorage.getItem(DONE_KEY) ?? "[]") as unknown;
    return new Set(Array.isArray(raw) ? (raw.filter((x) => x === "move" || x === "strike") as CoachStep[]) : []);
  } catch {
    return new Set();
  }
}

function saveDone(done: Set<CoachStep>): void {
  try {
    localStorage.setItem(DONE_KEY, JSON.stringify([...done]));
  } catch {
    /* sin almacenamiento: la pista puede repetirse, mejor eso que no enseñarla */
  }
}

export interface TouchCoach {
  /** El balon acaba de llegarte. */
  onBall(): void;
  /** Has golpeado (pase o tiro). */
  onKick(): void;
  dispose(): void;
}

export function createTouchCoach(shell: HTMLElement): TouchCoach {
  const done = loadDone();
  const root = document.createElement("div");
  root.className = "touch-coach";
  root.setAttribute("aria-hidden", "true");
  shell.appendChild(root);

  const timers = new Set<ReturnType<typeof setTimeout>>();
  const later = (ms: number, fn: () => void): void => {
    const id = setTimeout(() => {
      timers.delete(id);
      fn();
    }, ms);
    timers.add(id);
  };

  let showing: { step: CoachStep; el: HTMLElement } | null = null;

  const hide = (): void => {
    if (!showing) return;
    const el = showing.el;
    showing = null;
    el.classList.add("is-leaving");
    later(260, () => el.remove());
  };

  const finish = (step: CoachStep): void => {
    if (done.has(step)) return;
    done.add(step);
    saveDone(done);
    if (showing?.step === step) hide();
  };

  const show = (step: CoachStep, html: string): void => {
    if (done.has(step) || showing) return;
    const el = document.createElement("div");
    el.className = `touch-coach__hint touch-coach__hint--${step}`;
    el.innerHTML = html;
    root.appendChild(el);
    showing = { step, el };
    later(HINT_MS, () => {
      if (showing?.el === el) hide();
    });
  };

  /** Donde van las cosas en ESTA pantalla (los botones se pueden mover). */
  const geometry = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const layout = layoutTouch(width, height, touchPrefs(), readSafeInsets());
    const left = touchPrefs().leftHanded;
    return { width, height, layout, left };
  };

  const moveHint = (): void => {
    const { height, layout } = geometry();
    const x = (layout.stickFrom + layout.stickTo) / 2;
    const y = height * 0.66;
    show(
      "move",
      `<div class="touch-coach__stick" style="left:${x}px;top:${y}px">
         <i class="touch-coach__thumb touch-coach__thumb--orbit"></i>
       </div>
       <span class="touch-coach__label" style="left:${x}px;top:${y - 92}px">${t("Arrastra aquí para moverte")}</span>`,
    );
  };

  const strikeHint = (): void => {
    const { width, height, layout, left } = geometry();
    const k = layout.kick;
    // El campo: hacia la porteria rival, lejos de los dos pulgares.
    const fx = left ? width * 0.3 : width * 0.7;
    const fy = height * 0.34;
    show(
      "strike",
      `<i class="touch-coach__pulse" style="left:${k.cx}px;top:${k.cy}px;--d:${k.size + 34}px"></i>
       <span class="touch-coach__label touch-coach__label--kick" style="left:${k.cx}px;top:${k.cy - k.size / 2 - 40}px">
         ${t("Toca: pase · Mantén: tiro")}
       </span>
       <i class="touch-coach__thumb touch-coach__thumb--tap" style="left:${fx}px;top:${fy}px"></i>
       <span class="touch-coach__label" style="left:${fx}px;top:${fy + 52}px">${t("…o toca el campo: el balón va ahí")}</span>`,
    );
  };

  const onStick = (): void => {
    // Un momento para que se vea que ha funcionado, y fuera.
    later(500, () => finish("move"));
  };
  shell.addEventListener("golazo-touch:stick", onStick);

  // Empieza por moverse: es lo primero que se hace y lo que menos se adivina.
  later(900, moveHint);

  return {
    onBall(): void {
      if (done.has("strike")) return;
      // Si todavia se esta enseñando a moverse, el golpeo espera a la siguiente.
      if (showing?.step === "move") return;
      strikeHint();
    },
    onKick(): void {
      finish("strike");
    },
    dispose(): void {
      shell.removeEventListener("golazo-touch:stick", onStick);
      for (const id of timers) clearTimeout(id);
      timers.clear();
      root.remove();
    },
  };
}
